'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { motion, useInView } from "framer-motion";

import {
  FaCheckCircle,
  FaRocket,
  FaCogs,
  FaStar,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import OrderModal from '../shared/OrderModal';
import OrderSuccessModal from '../pricing/OrderSuccessModal';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const Hero = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [swiperInstance, setSwiperInstance] = useState(null);
  
  const images = [

    '/image/sparkle-clean.webp',
    '/image/hero-image1.webp',
    '/image/hero-image2.webp',
    '/image/hero-image3.webp'
  ];

  const handleScrollToBenefits = () => {
    document.getElementById('benefits-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const generateOrderId = () => `${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  const handleOrderSubmit = details => {
    setOrderId(generateOrderId());
    setIsOrderModalOpen(false);
    setIsSuccessModalOpen(true);
  };

  return (
    <section className="relative pt-20 sm:pt-16 lg:pt-28 xl:pt-32 dark:bg-dark-background overflow-hidden">

{/* Pure CSS Tech Pattern Background */}
<div className="absolute inset-0 opacity-20 pointer-events-none">
  {/* Light‑mode (default): dark grid on white */}
  <div
    className="absolute inset-0 bg-[length:40px_40px]
               dark:hidden"
    style={{
      backgroundImage: `
        linear-gradient(to right, rgba(0,0,0,0.1) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px),
        linear-gradient(45deg, rgba(0,0,0,0.05) 25%, transparent 25%, transparent 50%, rgba(0,0,0,0.05) 50%, rgba(0,0,0,0.05) 75%, transparent 75%)
      `,
      backgroundSize: `
        40px 40px,
        40px 40px,
        4px 4px
      `,
      maskImage: `linear-gradient(to bottom, transparent 10%, #000 40%, #000 60%, transparent 90%)`,
      WebkitMaskImage: `linear-gradient(to bottom, transparent 10%, #000 40%, #000 60%, transparent 90%)`,
    }}
  >
    <motion.div
      className="absolute inset-0 bg-[length:80px_80px]"
      animate={{ x: [0, 80], y: [0, 80] }}
      transition={{
        duration: 20,
        repeat: Infinity,
        ease: "linear"
      }}
      style={{
        // backgroundImage: `
        //   linear-gradient(135deg, rgba(0,0,0,0.03) 25%, transparent 25%, transparent 50%, rgba(0,0,0,0.03) 50%, rgba(0,0,0,0.03) 75%, transparent 75%)
        // `,
        backgroundSize: "80px 80px"
      }}
    />
  </div>

  {/* Dark‑mode: white grid on black */}
  <div
    className="absolute inset-0 bg-[length:40px_40px]
               hidden dark:block"
    style={{
      backgroundImage: `
        linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px),
        linear-gradient(45deg, rgba(255,255,255,0.05) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.05) 75%, transparent 75%)
      `,
      backgroundSize: `
        40px 40px,
        40px 40px,
        4px 4px
      `,
      maskImage: `linear-gradient(to bottom, transparent 10%, #000 40%, #000 60%, transparent 90%)`,
      WebkitMaskImage: `linear-gradient(to bottom, transparent 10%, #000 40%, #000 60%, transparent 90%)`,
    }}
  >
    <motion.div
      className="absolute inset-0 bg-[length:80px_80px]"
      animate={{ x: [0, 80], y: [0, 80] }}
      transition={{
        duration: 20,
        repeat: Infinity,
        ease: "linear"
      }}
      style={{
        backgroundImage: `
          linear-gradient(135deg, rgba(255,255,255,0.03) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.03) 75%, transparent 75%)
        `,
        backgroundSize: "80px 80px"
      }}
    />
  </div>
</div>



      
      {/* BACKGROUND SHAPES & BLOBS */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute left-[calc(50%-20vw)] w-[120vw] aspect-[1155/678]
                     -translate-x-1/3 rotate-[30deg]
                     bg-gradient-to-tr from-primary-400/20 to-primary-400/5
                     opacity-15 dark:opacity-10
                     sm:left-[calc(50%-30vw)] sm:w-[150vw] lg:left-[calc(50%-40vw)]"
          style={{
            clipPath:
              'polygon(74.1% 44.1%,100% 61.6%,97.5% 26.9%,85.5% 0.1%,80.7% 2%,72.5% 32.5%,60.2% 62.4%,52.4% 68.1%,47.5% 58.3%,45.2% 34.5%,27.5% 76.7%,0.1% 64.9%,17.9% 100%,27.6% 76.8%,76.1% 97.7%,74.1% 44.1%)',
          }}
        />

        <div
          className="absolute inset-0 opacity-10 dark:opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='52' height='26' viewBox='0 0 52 26' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233B82F6' fill-opacity='0.1'%3E%3Cpath d='M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4v2c-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6zm25.464-1.95l8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: 'clamp(40px, 5vw, 60px)',
            backgroundPosition: 'center'
          }}
        />

        <div className="absolute w-[clamp(400px,80vw,800px)] h-[clamp(400px,80vw,800px)] -top-[20%] -left-[20%] bg-primary-300/20 rounded-full blur-3xl opacity-20 animate-float md:-top-[15%] md:-left-[15%]" />
        <div className="absolute w-[clamp(300px,60vw,600px)] h-[clamp(300px,60vw,600px)] -bottom-[15%] -right-[15%] bg-secondary-400/15 rounded-full blur-3xl opacity-20 animate-float-delayed" />
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <div className="text-center">
          <div className="inline-flex items-center bg-light-surface dark:bg-dark-surface rounded-full px-4 py-2 mb-6 sm:mb-8 shadow-md hover:shadow-lg transition-shadow duration-200 border border-light-border dark:border-dark-border">
            <span className="relative flex h-3 w-3 mr-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-full w-full bg-primary-500" />
            </span>
            <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              Affordable one-time fee - $0 monthly costs
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-bold text-light-text-primary dark:text-dark-text-primary max-w-3xl mx-auto leading-tight mb-6">
          Websites That Book Cleaning Jobs{' '}
            <span className="bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text text-transparent">
             While You Sleep
            </span>
          </h1>

          <p className="text-base sm:text-lg text-light-text-secondary dark:text-dark-text-secondary max-w-2xl mx-auto mb-8 sm:mb-10">
            Get your professional 24/7 booking website in{' '}
            <strong className="font-semibold text-primary-600">7 days</strong> – attract more clients and grow your cleaning business.
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 z-50">
            <button
              onClick={() => setIsOrderModalOpen(true)}
              className="flex items-center px-8 py-3.5 text-base font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-xl shadow-lg transition-colors duration-200 transform hover:scale-[1.02]"
            >
              <FaRocket className="mr-2 text-xl" />
              Start Now – $249
            </button>

            <Link
              href="#benefits-section"
              className="flex items-center px-8 py-3.5 text-base font-medium text-light-text-primary dark:text-dark-text-primary border-2 border-primary-500 hover:bg-primary-500/10 dark:hover:bg-primary-500/20 rounded-xl transition-colors duration-200"
            >
              <FaCogs className="mr-2 text-xl" />
              See How It Works
            </Link>
          </div>

          {/* Swiper Carousel */}
          <div className="relative mt-12 sm:mt-16 max-w-6xl mx-auto z-20">
            <div className="relative aspect-square sm:aspect-[1.78] rounded-2xl overflow-hidden shadow-2xl group border border-light-border dark:border-dark-border">
              <Swiper
                modules={[Autoplay, Navigation, Pagination]}
                speed={800}
                loop={true}
                autoplay={{
                  delay: 5000,
                  disableOnInteraction: false,
                }}
                pagination={{
                  clickable: true,
                  el: '.hero-pagination',
                  bulletClass: 'swiper-pagination-bullet !bg-white/50 !w-3 !h-3',
                  bulletActiveClass: '!bg-primary-500',
                }}
                navigation={{
                  prevEl: '.hero-prev',
                  nextEl: '.hero-next',
                }}
                onSwiper={setSwiperInstance}
                className="w-full h-full"
              >
                {images.map((img, index) => (
                  <SwiperSlide key={index}>
                    <div className="relative w-full h-full">
                      <img
                        src={img}
                        alt={`Cleaning website example ${index + 1}`}
                        className="w-full h-full object-cover transform  transition-transform duration-300"
                        loading="eager"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-600/10 to-transparent dark:from-primary-600/15" />
                    </div>
                  </SwiperSlide>
                ))}

                {/* Navigation Arrows */}
                <button className="hero-prev absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/30 hover:bg-black/50 rounded-full transition-colors duration-200">
                  <FaChevronLeft className="text-white text-xl" />
                </button>
                <button className="hero-next absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/30 hover:bg-black/50 rounded-full transition-colors duration-200">
                  <FaChevronRight className="text-white text-xl" />
                </button>

                {/* Pagination Dots */}
                <div className="hero-pagination absolute bottom-8 right-4 flex gap-2 z-10 !w-auto" />
              </Swiper>

              {/* Stats Bar */}
              <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-light-surface/95 dark:bg-dark-surface/95 shadow-md flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0 backdrop-blur-sm z-20">
                <div className="flex items-center gap-2">
                  <FaCheckCircle className="text-primary-500 text-lg" />
                  <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                    3 New Bookings Today
                  </span>
                </div>
                <div className="flex items-center gap-2">
<FaStar className="text-yellow-400 text-lg" />
                  <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                    4.9/5 Rating
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

  {/* Modern Scroll Indicator */}
{!isScrolled && (
  <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
    <button
      onClick={handleScrollToBenefits}
      aria-label="Scroll to benefits section"
      className="relative flex flex-col items-center text-white transition-transform duration-300 hover:scale-105 focus:outline-none"
    >
    
      
      {/* Inner solid circle with icon */}
      <span className="relative flex items-center justify-center w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm ring-1 ring-white/40 drop-shadow-lg">
        <FaChevronDown className="text-2xl" />
      </span>
      
      {/* Label */}
      <span className="mt-2 relative text-sm font-semibold uppercase tracking-wider">
        See Benefits
      </span>
    </button>
  </div>
)}

      {/* Modals */}
      {isOrderModalOpen && (
        <OrderModal
          onClose={() => setIsOrderModalOpen(false)}
          onSubmit={handleOrderSubmit}
          selectedPlan={{ name: 'Basic Plan', price: 249, desc: 'Get started with a website.' }}
          discount={0}
          calculatePrice={p => p}
        />
      )}
      {isSuccessModalOpen && (
        <OrderSuccessModal orderId={orderId} onClose={() => setIsSuccessModalOpen(false)} />
      )}
    </section>
  );
};

export default Hero;